<script setup>
import { Power4, gsap } from 'gsap'
import 'vue3-carousel/dist/carousel.css'
import { vElementSize } from '@vueuse/components'
import { RichTextRenderer } from '@marvr/storyblok-rich-text-vue-renderer'

const props = defineProps({ blok: Object })
const { t, locale } = useI18n()
const top = ref(null)
const route = useRoute()
const mySearch = ref('')
const myCategories = ref('')
const myLocation = ref('')
const activeIndex = ref(null)
const activeTabIndex = ref(0)
const activeLocation = ref(0)
const activeCareerType = ref(0)
const selectedCareerType = ref('1c2d6684-2943-4b67-9cfd-abd8e7f88f76')
const selected = ref('')
const perPage = ref(3)
const getHeight = ref(0)
const myClass = ref('')
const hero = ref({
  header: props.blok.hero_header,
  title: props.blok.hero_title,
  subtitle: props.blok.hero_subtitle,
  url: props?.blok?.media?.filename,
})
const target = ref(null)
const targetIsVisible = ref(false)

const { stop } = useIntersectionObserver(
  target,
  ([{ isIntersecting }], observerElement) => {
    if (isIntersecting)
      targetIsVisible.value = isIntersecting
  },
)

const { all_positions, loading, fetchAllPositions } = usePositions()
const { fetchJobLocation, fetchJobCategories, fetchJobTime, fetchJobLevel, formattedJobLocation, formattedJobCategories, formattedJobTime, formattedJobLevel } = useJobCategories()

await Promise.all([
  fetchAllPositions(),
  fetchJobLocation(),
  fetchJobCategories(),
  fetchJobTime(),
  fetchJobLevel(),
])

function compare(a, b) {
  if (a.type < b.type)
    return -1
  if (a.type > b.type)
    return 1
  return 0
}
const sortPositions = computed(() => {
  return all_positions.value.sort(compare)
})

const filterCareerType = computed(() => {
  return sortPositions.value.filter((item) => {
    return item.content.job_level === selectedCareerType.value
  })
})

const filterPosition = computed(() => {
  return filterCareerType.value.filter((item) => {
    if (selected.value === '') {
      if (myCategories.value === '93e8f350-348c-4602-ab12-e0e1ffbd0956' && myLocation.value === '010709b2-1535-4c4e-a180-cc80b687ff83')
        return item && item.name.toLowerCase().includes(mySearch.value.toLowerCase())
      else if (myCategories.value === '93e8f350-348c-4602-ab12-e0e1ffbd0956')
        return item && myLocation.value === item.content.job_location && item.name.toLowerCase().includes(mySearch.value.toLowerCase())
      else
        return myLocation.value === item.content.job_location && myCategories.value === item.content.job_categories && item.name.toLowerCase().includes(mySearch.value.toLowerCase())
    }
    else {
      if (selected.value === '93e8f350-348c-4602-ab12-e0e1ffbd0956' && myLocation.value === fitleredUid())
        return item && myLocation.value === item.content.job_location
      else if (selected.value === '93e8f350-348c-4602-ab12-e0e1ffbd0956' && myLocation.value !== fitleredUid() && myLocation.value === '010709b2-1535-4c4e-a180-cc80b687ff83')
        return item
      else if (selected.value !== '93e8f350-348c-4602-ab12-e0e1ffbd0956' && myLocation.value !== fitleredUid() && myLocation.value === '010709b2-1535-4c4e-a180-cc80b687ff83')
        return item && selected.value === item.content.job_categories
      else if (selected.value === '93e8f350-348c-4602-ab12-e0e1ffbd0956' && myLocation.value !== fitleredUid())
        return item && myLocation.value === item.content.job_location
      else
        return selected.value === item.content.job_categories && myLocation.value === item.content.job_location
    }
  })
})

function fitleredUid() {
  // eslint-disable-next-line no-unreachable-loop
  for (let i = 0; i < all_positions.value.length; i++) {
    const data = all_positions.value[i]
    return data.content.job_location
  }
}

const paginatedData = computed(() => {
  return filterPosition.value.slice(0, perPage.value)
})

onMounted(() => {
  mySearch.value = ''
  myCategories.value = '93e8f350-348c-4602-ab12-e0e1ffbd0956'
  myLocation.value = '010709b2-1535-4c4e-a180-cc80b687ff83'
})

function mouseHover(index) {
  activeIndex.value = index
  myClass.value = '!transition-all !duration-300 !ease-in-out !delay-0'
}

function mouseOut() {
  myClass.value = ''
}

function onSelectLocation(item, index) {
  activeLocation.value = index
  myLocation.value = item.uuid
}

function tabActive(categories, index) {
  activeTabIndex.value = index
  selected.value = categories.uuid
  myCategories.value = null
  mySearch.value = ''
}

function showMore() {
  if (perPage.value < filterPosition.value.length)
    perPage.value += 12
  else
    perPage.value = filterPosition.value.length
}

function scrollToElement() {
  target.value.scrollIntoView({ behavior: 'smooth' })
}

function hideMore() {
  perPage.value = 3
  target.value.scrollIntoView({ behavior: 'smooth' })
  if (perPage.value === 3) {
    setTimeout(() => {
      scrollToElement()
    }, 100)
  }
}

function scrollToTop() {
  top.value.scrollIntoView({ behavior: 'smooth' })
}

function handleClick(search, categories, location) {
  scrollToElement()
  selected.value = ''
  mySearch.value = search
  myCategories.value = categories
  myLocation.value = location
  activeTabIndex.value = null
  activeIndex.value = null
}

function onResize({ height }) {
  getHeight.value = height
}

function onEnter(el, done) {
  gsap.to(el, 0.1, {
    t: 0,
    autoAlpha: 1,
    ease: Power4.easeOut,
    onComplete: done,
  }, {
    t: -10,
    autoAlpha: 0,
    ease: Power4.easeOut,
    onComplete: done,
  })
  done()
}
function onLeave(el, done) {
  gsap.to(el, 0.1, {
    t: 0,
    autoAlpha: 0,
    ease: Power4.easeOut,
    onComplete: done,
  }, {
    t: -10,
    autoAlpha: 1,
    ease: Power4.easeOut,
    onComplete: done,
  })
  done()
}

function onBeforeEnter(el) {
  el.style.opacity = 0
}

const careerTypeOptions = reactive([
  {
    value: 'Experienced Professional',
    match: '1c2d6684-2943-4b67-9cfd-abd8e7f88f76',
    svg: `<svg width="22" height="21" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21.7345 20.5432H15.5806V0.543213H21.7345V20.5432ZM14.0422 20.5432H7.88832V6.69706H14.0422V20.5432ZM6.34986 20.5432H0.196014V11.3124H6.34986V20.5432Z" fill="currentColor" /></svg>`,
  },
  {
    value: 'New Graduate',
    match: '0cc0346d-7f6e-40df-9b49-3069adb1bb18',
    svg: `<svg width="23" height="21" viewBox="0 0 23 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.2345 20.5432H16.0806V0.543213H22.2345V20.5432ZM17.6191 19.0048H20.696V2.08167H17.6191V19.0048ZM14.5422 20.5432H8.38832V6.69706H14.5422V20.5432ZM6.84986 20.5432H0.696014V11.3124H6.84986V20.5432Z" fill="currentColor"/>
    </svg>`,
  },
  {
    value: 'Student Trainee / Internship',
    match: '61317551-394f-414f-b204-9dc20f3dbd4e',
    svg: `<svg width="22" height="21" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21.7344 20.5432H15.5806V0.543213H21.7344V20.5432ZM17.1191 19.0048H20.196V2.08167H17.1191V19.0048ZM14.0421 20.5432H7.88829V6.69706H14.0421V20.5432ZM9.42675 19.0048H12.5037V8.23552H9.42675V19.0048ZM6.34983 20.5432H0.195984V11.3124H6.34983V20.5432Z" fill="currentColor"/>
    </svg>`,
  },
])

function onSelectCareerType(type, index) {
  activeCareerType.value = index
  selectedCareerType.value = type.match
  scrollToElement()
}
</script>

<template>
  <div class="relative overflow-hidden">
    <div v-if="route.name === `career-at-dexon___${locale}`" ref="top" class="lg:pb-desktop">
      <div class="relative mx-auto w-full">
        <div
          class="relative flex flex-col items-center lg:flex-row"
        >
          <HeroCareer :content="hero">
            <template #default>
              <div class="relative z-1 mt-8 rounded-3 p-2 shadow-custom-lg">
                <h4 class="text-secondary mb-2 flex items-center gap-2 text-base text-primary font-semibold">
                  <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-width="1.5"><path d="M11.811 6.727C12.825 4.909 13.331 4 14.09 4c.757 0 1.264.909 2.277 2.727l.262.47c.288.517.432.775.657.945c.224.17.504.234 1.063.36l.51.116c1.967.445 2.95.667 3.185 1.42c.234.753-.437 1.537-1.778 3.106l-.347.406c-.381.445-.572.668-.658.944c-.086.276-.057.573 0 1.168l.053.541c.203 2.094.305 3.14-.308 3.605c-.613.465-1.534.041-3.377-.807l-.476-.22c-.524-.24-.786-.361-1.063-.361c-.278 0-.54.12-1.063.361l-.477.22c-1.842.848-2.763 1.272-3.376.807c-.613-.465-.511-1.511-.309-3.605l.053-.541c.057-.595.086-.892 0-1.168c-.085-.276-.276-.498-.657-.944l-.347-.406C6.57 11.575 5.9 10.79 6.135 10.038c.234-.753 1.218-.975 3.185-1.42l.51-.116c.559-.126.838-.19 1.063-.36c.224-.17.368-.428.656-.945z" /><path stroke-linecap="round" d="M2.089 16a4.736 4.736 0 0 1 4-.874m-4-4.626c1-.5 1.29-.44 2-.5M2 5.609l.208-.122c2.206-1.292 4.542-1.64 6.745-1.005l.208.06" opacity=".5" /></g></svg>
                  </div>
                  <span>Discover your career at dexon</span>
                </h4>
                <div class="w-full flex flex-col gap-2 sm:flex-row">
                  <div
                    v-for="(careerType, index) in careerTypeOptions" :key="careerType"
                    class="group flex flex-auto flex-col cursor-pointer items-center gap-3 border rounded-2 bg-white px-4 py-6 text-center transition-all"
                    :class="activeCareerType === index ? 'border-secondary-red-100' : 'border-slate-200 hover:border-slate-400 active:!ring-2 active:!ring-slate-500/20'"
                    @click="onSelectCareerType(careerType, index)"
                  >
                    <div class="transition-all" :class="activeCareerType === index ? 'text-secondary-red-100' : 'text-slate-500 group-hover:text-slate-700'" v-html="careerType.svg" />
                    <span class="transition-all 2xl:whitespace-nowrap" :class="activeCareerType === index ? 'text-secondary-red-100' : 'text-slate-500 group-hover:text-slate-700'">{{ careerType.value }}</span>
                  </div>
                </div>
              </div>
            </template>
          </HeroCareer>
        </div>
      </div>
    </div>

    <div ref="target" class="py-mobile lg:py-desktop">
      <div class="container">
        <div
          :class="[targetIsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[-15px]']"
          class="transition-[transform,opacity] mb-1 text-md text-secondary-red-100 font-bold capitalize duration-700 delay-200 ease-in-out lg:text-center lg:text-xl md:text-xl sm:text-lg"
        >
          {{ blok.header }}
        </div>
        <h2
          :class="[targetIsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[-15px]']"
          class="transition-[transform,opacity] mx-auto mb-custom-xl text-2xl font-bold duration-700 delay-400 ease-in-out lg:max-w-3xl lg:text-center md:text-4xl sm:text-3xl xl:text-5xl"
        >
          {{ blok.title }}
        </h2>
        <div :class="[targetIsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[15px]']" class="transition-[transform,opacity] flex flex-wrap gap-2 pb-custom-lg duration-700 delay-600 sm:justify-center">
          <div
            v-for="(item, index) in formattedJobLocation" :key="index"
            class="flex cursor-pointer items-center gap-3 rounded-[4px] px-3 py-2 transition duration-200 ease-linear"
            :class="item.uuid === myLocation ? ' shadow-custom-lg' : 'bg-white'"
            @click="onSelectLocation(item, index)"
          >
            <div v-if="item.icon" v-html="item.icon" />
            <span class="text-base font-medium">{{ item.value }}</span>
          </div>
        </div>
        <div class="sticky flex flex-wrap gap-x-6 gap-y-3 pb-custom-lg lg:gap-y-0 md:gap-x-10">
          <div
            v-for="(categories, index) in formattedJobCategories"
            :key="categories.uuid" :class="[
              targetIsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-[-15px]',
              `transition-[transform,opacity] duration-300 delay-${100 * (index + 1)}`,
            ]"
          >
            <span
              :class="activeTabIndex === index || myCategories === categories.uuid ? 'text-secondary-red-100' : 'text-slate-400'"
              class="cursor-pointer text-md font-semibold capitalize md:text-lg"
              @click="tabActive(categories, index)"
            >
              {{ categories.value }}
            </span>
          </div>
        </div>
        <div
          class="relative min-h-[452px] flex flex-col"
          :style="{
            minHeight: `${getHeight}px`,
          }"
        >
          <TransitionGroup
            v-if="paginatedData.length > 0"
            v-element-size="onResize"
            :css="false"
            name="list"
            tag="ul" class="grid gap-y-4 pl-0"
            @enter="onEnter"
            @leave="onLeave"
            @before-enter="onBeforeEnter"
          >
            <CardPositionCard
              v-for="(detail, index) in paginatedData" :key="detail.uuid"
              class="transition-[transform,opacity] duration-300"
              :class="[
                targetIsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-[-15px]', myClass,
              ]"
              :style="{
                transitionDelay: `${5 * (index + 1)}ms`,
              }"
              :detail="detail.content"
              :location="formattedJobLocation"
              :time="formattedJobTime"
              :level="formattedJobLevel"
              :slug="detail.full_slug"
              :active-index="activeIndex"
              :date="detail.created_at"
              :index="index"
              :loading="loading"
              @mousemove="mouseHover(index)"
              @mouseleave="mouseOut"
            />
          </TransitionGroup>
          <div
            v-else-if="paginatedData.length <= 0"
            :class="[
              targetIsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-[-15px]',
            ]" class="transition-[transform,opacity] transition-[transform,opacity] relative z-10 w-full flex flex-col cursor-pointer justify-center rounded-lg bg-white px-4 py-custom-xl text-center transition duration-200 duration-300 duration-700 delay-200 ease-in-out ease-linear md:flex-row md:items-center"
          >
            <span class="text-3xl text-secondary-gray-400 font-medium">This position is not currently open</span>
          </div>
          <div v-else class="md:justify-normal relative z-10 w-full flex flex-col cursor-pointer justify-center rounded-lg bg-white p-5 transition duration-200 ease-linear md:flex-row md:items-center">
            <UISpinner class="mx-auto min-h-xs" />
          </div>
        </div>
        <div
          v-if="paginatedData.length > 0"
          :class="[targetIsVisible && paginatedData.length > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[15px]']"
          class="transition-[transform,opacity] my-custom-xl cursor-pointer text-center text-secondary-red-100 font-bold duration-700 delay-200 ease-in-out"
        >
          <div v-if="perPage >= 3 && filterPosition.length !== paginatedData.length" class="flex items-center justify-center gap-x-2" @click="showMore">
            <span>{{ t('career.page.show') }}</span><div class="h-[20px] flex items-center">
              <svg class="text-secondary-red-100 transition-transform duration-200 ease-in-out" width="20" height="20" viewBox="0 0 32 32"><path fill="currentColor" d="M16 22L6 12l1.4-1.4l8.6 8.6l8.6-8.6L26 12z" /></svg>
            </div>
          </div>
          <div v-else-if="paginatedData.length > 3" class="flex items-center justify-center gap-x-2" @click="hideMore">
            <span>{{ t('career.page.hide') }}</span><div class="h-[20px] flex items-center">
              <svg class="rotate-180 text-secondary-red-100 transition-transform duration-200 ease-in-out" width="20" height="20" viewBox="0 0 32 32"><path fill="currentColor" d="M16 22L6 12l1.4-1.4l8.6 8.6l8.6-8.6L26 12z" /></svg>
            </div>
          </div>
        </div>
        <div v-if="blok.components">
          <div v-for="component in blok.components" v-if="blok.components?.length" :key="component.uuid">
            <StoryblokComponent :blok="component" :theme="blok.color" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="route.name === `career-at-dexon___${locale}`" class="container">
      <div class="h-[1px] w-full bg-slate-200" />
      <div class="py-mobile lg:py-desktop">
        <div class="relative">
          <div
            class="mb-1 text-md text-secondary-red-100 font-bold capitalize lg:text-xl md:text-xl sm:text-lg"
          >
            {{ blok.header2 }}
          </div>
          <h2 class="mx-auto mb-custom-md text-3xl font-bold md:text-4xl xl:text-5xl">
            {{ blok.title2 }}
          </h2>
          <div v-if="blok" class="columns-content mb-custom-md text-xl leading-[1.8]">
            <RichTextRenderer :document="blok.content2" />
          </div>
          <div class="max-w-[250px]">
            <NuxtLink
              class="group text-xl text-secondary-red-100 font-bold"
              :to="localePath({
                path: '/aboutus',
              })"
            >
              <div class="flex items-center gap-x-2">
                <span>{{ t('button.aboutus') }}</span>
                <div class="transition duration-200 ease-in-out group-hover:translate-x-2">
                  <svg class="text-secondary-red-100 -rotate-90" width="20" height="20" viewBox="0 0 32 32" data-v-inspector="components\Card\PositionCard.vue:223:13"><path fill="currentColor" d="M16 22L6 12l1.4-1.4l8.6 8.6l8.6-8.6L26 12z" data-v-inspector="components\Card\PositionCard.vue:223:138" /></svg>
                </div>
              </div>
            </NuxtLink>
          </div>
        </div>
      </div>
      <div class="h-[1px] w-full bg-slate-200" />
      <div class="relative py-mobile lg:py-desktop">
        <UIHexagon custom-class="top-0 right-0 translate-y-[12%] lg:translate-x-[35%] translate-x-[50%]  rotate-60" size="w-[375px] h-[375px]" />
        <div class="relative">
          <div
            class="mb-1 text-md text-secondary-red-100 font-bold capitalize lg:text-xl md:text-xl sm:text-lg"
          >
            {{ blok.header3 }}
          </div>
          <h2 class="mx-auto mb-custom-xl text-3xl font-bold md:text-4xl xl:text-5xl">
            {{ blok.title3 }}
          </h2>
          <div class="grid gap-x-custom-lg gap-y-custom-xl overflow-auto border-t-4 border-t-secondary-red-100 rounded-md bg-white p-8 shadow-custom-xl shadow-shadow-light/10 md:grid-cols-2 sm:p-12">
            <div>
              <div class="mb-custom-md flex items-center gap-x-3">
                <svg class="h-8 w-8" xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38"><g id="Group_844"><path id="Path_1098" d="m36.2,9v13.6h-3.6v-11.8l-9-6.3-9,6.3v2.7h-3.6v-4.5L23.5,0l12.7,9Z" fill="#ee2334" /><rect id="Rectangle_240" x="24.4" y="10" width="1.8" height="1.8" fill="#140a4b" /><rect id="Rectangle_241" x="20.8" y="10" width="1.8" height="1.8" fill="#140a4b" /><rect id="Rectangle_242" x="24.4" y="13.6" width="1.8" height="1.8" fill="#140a4b" /><rect id="Rectangle_243" x="20.8" y="13.6" width="1.8" height="1.8" fill="#140a4b" /><path id="Path_1099" d="m32.6,26.2h-3.6c0-2.2-1.4-4.1-3.4-4.9l-11.1-4.2H0v19.9h10.9v-2.6l12.7,3.5,14.5-4.5v-1.8c0-3-2.4-5.4-5.4-5.4h0Zm-25.3,7.2h-3.6v-12.7h3.6v12.7Zm16.2.7l-12.6-3.5v-9.9h2.9l10.5,3.9c.6.2,1,.8,1,1.5-1.4,0-2.8-.1-4.2-.3l-4.3-1.4-1.1,3.4,4.3,1.4c.9.3,1.9.5,2.9.5h9.7c.7,0,1.3.4,1.6,1l-10.7,3.3Z" fill="#140a4b" /></g></svg>
                <h3 class="text-md font-bold capitalize lg:text-xl md:text-lg">
                  {{ blok.table.thead[0].value }}
                </h3>
              </div>
              <ul v-for="tbody in blok.table.tbody" :key="tbody._uid">
                <li v-if="tbody.body[0].value" class="list-disc">
                  {{ tbody.body[0].value }}
                </li>
              </ul>
            </div>
            <div>
              <div class="mb-custom-md flex items-center gap-x-3">
                <svg class="h-8 w-8" xmlns="http://www.w3.org/2000/svg" width="40.2" height="38.1" viewBox="0 0 40.2 38.1"><g id="Group_849"><path id="Path_1100" d="m40.2,34.1c0,2.2-1.8,4-4,4,0,0,0,0,0,0H4C1.8,38.2,0,36.4,0,34.2c0,0,0,0,0,0v-10h40.2v10Z" fill="none" /><path id="Path_1101" d="m36.1,8h-4.4c.2-.6.4-1.3.4-2C32.1,2.7,29.4,0,26.1,0h0c-2,0-3.9,1-5,2.7l-1,1.3-1-1.4C18,1,16.1,0,14.1,0c-3.3,0-6,2.7-6,6,0,0,0,0,0,0,0,.7.1,1.4.4,2h-4.4C1.8,8,0,9.8,0,12c0,0,0,0,0,0v12s4,0,4,0v-12h10.2l-4.2,5.7,3.3,2.3,6.8-9.2,6.8,9.2,3.3-2.3-4.2-5.7h10.2v12h4v-12c0-2.2-1.8-4-4-4,0,0,0,0,0,0Zm-22.1,0c-1.1,0-2-.9-2-2,0-1.1.9-2,2-2,1.1,0,2,.9,2,2,0,1.1-.9,2-2,2h0Zm12,0c-1.1,0-2-.9-2-2,0-1.1.9-2,2-2,1.1,0,2,.9,2,2,0,1.1-.9,2-2,2h0Z" fill="#140a4b" /><path id="Path_1102" d="m0,24.1v10c0,2.2,1.8,4,4,4,0,0,0,0,0,0h32.1c2.2,0,4-1.8,4-4,0,0,0,0,0,0v-10H0Zm36.1,10H4v-4h32.1v4Z" fill="#ee2334" /></g></svg>
                <h3 class="text-md font-bold capitalize lg:text-xl md:text-lg">
                  {{ blok.table.thead[1].value }}
                </h3>
              </div>
              <ul v-for="tbody in blok.table.tbody" :key="tbody._uid">
                <li v-if="tbody.body[1].value" class="list-disc">
                  {{ tbody.body[1].value }}
                </li>
              </ul>
            </div>
            <div>
              <UIButton class="rounded-md" @on-click="scrollToTop">
                {{ $t('button.findjobs') }}
              </UIButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
